import { Link, useParams } from 'react-router-dom';

import { DataGate } from '../lib/DataContext.tsx';
import type { FestivalData, NewsItem, NewsletterSection } from '../lib/types.ts';
import { TopNav } from '../components/sections/TopNav.tsx';
import { Footer } from '../components/sections/Footer.tsx';
import { CornerGuitar } from '../components/CornerGuitar.tsx';
import { CmsNewsletterSection } from '../components/sections/CmsNewsletterSection.tsx';
import { AppBand } from '../components/sections/AppBand.tsx';
import { RICHTEXT, EYEBROW, OUTLINE_CONTROL, themeOf } from '../components/sections/cmsTheme.ts';
import { cdnVariant, cdnSrcSet, cdnWideUrl } from '../lib/cdn.ts';
import { formatNewsDate } from '../application/festivalSelectors.ts';
import { usePageMeta } from '../lib/usePageMeta.ts';

/** The story page runs on the white combination, so its ink, hairlines, image tone and accent
 *  come from the same map every CMS section uses (CMS v2). */
const T = themeOf('white');

/** The article's reading measure. The image spans the full 1200px content width, but body copy
 *  at that width is unreadable, so title/date/body sit in a centered ~768px column. */
const MEASURE = 'max-w-3xl mx-auto';

/**
 * Newsletter + app blocks shown at the foot of every news story. The heading and intro
 * mirror the newsletter on the /nyheder overview page; the background uses the grey theme
 * here. The AppBand below is a fixed (non-configurable) band, rendered without its guitar.
 */
const NEWSLETTER: NewsletterSection = {
  type: 'newsletter',
  theme: 'grey',
  guitar: null,
  heading: 'Tilmeld dig nyhedsmailen og følg med i, hvad der sker – lidt før alle andre.',
  buttonLabel: 'Tilmeld',
  signupUrl:
    'https://midtfynsfestival.us18.list-manage.com/subscribe?u=0452a756b9bac7cef4b0ac602&id=06d5c9c119',
};

/**
 * Fixed (non-CMS) page for viewing a single news story at /nyheder/:id.
 *
 * Layout (CMS v2): the 16:9 image spans the 1200px content width with the 4px card radius, then
 * the article itself runs as one left-aligned column at a reading measure — the publish date as
 * an accent eyebrow above the title, the title, a hairline, then the body. It closes with a
 * "Tilbage" outline pill, since that is navigation away from the article rather than its CTA.
 *
 * - The title may be long, so it scales, wraps (balanced) and never truncates.
 * - The article body may contain <h2>/<h3> (styled by the shared RICHTEXT).
 * - The page ends with the newsletter and app promo bands, as on /nyheder.
 */
export default function NewsStoryPage() {
  return <DataGate>{(data) => <NewsStoryContent data={data} />}</DataGate>;
}

function NewsStoryContent({ data }: { data: FestivalData }) {
  const { id } = useParams<{ id: string }>();
  // A story is reachable by its slug and, for anything published before slugs existed or shared
  // as a guid link, still by its id.
  const item = data.news.items.find(n => n.slug === id) ?? data.news.items.find(n => n.id === id);

  // The story's own title and opening lines, so a shared link says what the article is about
  // rather than repeating the front page. A story that does not exist says so, and stays out of
  // search results.
  usePageMeta({
    title: item ? item.title : 'Nyheden findes ikke',
    description: item?.htmlContent,
    noIndex: !item,
  });

  if (!item) {
    return (
      <>
        <TopNav mode="subpage" />
        <section className="bg-mf-yellow py-10 md:py-16">
          <div className="max-w-3xl mx-auto px-5 md:px-8 text-center">
            <h1 className="font-display text-mf-black text-3xl md:text-[40px] leading-none tracking-[-0.02em]">
              Nyheden findes ikke
            </h1>
            <p className="mt-4 text-mf-black/80">Opslaget kan være fjernet eller flyttet.</p>
            <div className="mt-8">
              <BackLink />
            </div>
          </div>
        </section>
        <Footer />
      </>
    );
  }

  return <NewsStoryView item={item} />;
}

/**
 * Presentational news story — the full page for one article, given the item. Separated from the
 * routed page so the internal preview gallery (/intern/preview) can render it with sample data.
 */
export function NewsStoryView({ item }: { item: NewsItem }) {
  // Use the CDN's real 16:9 rendition rather than cropping the 3:2 source into a 16:9 frame.
  const heroImageUrl = cdnWideUrl(item.imageUrl);
  return (
    <>
      <TopNav mode="subpage" />

      {/* The article is the page: a landmark around it, so "skip to content" has somewhere to go. */}
      <main id="indhold">
      <section className={`relative overflow-hidden ${T.bg} pb-10 md:pb-16`}>
        <CornerGuitar corner="bottom-left" colorClass={T.guitar} opacityClass="opacity-10" />
        <div className="relative max-w-[1200px] mx-auto px-5 md:px-8">
          {item.imageUrl && (
            <div className={`aspect-16/9 w-full overflow-hidden rounded-card mb-8 md:mb-12 ${T.img}`}>
              <img
                src={cdnVariant(heroImageUrl, 'desktop')}
                srcSet={cdnSrcSet(heroImageUrl) || undefined}
                sizes="(min-width: 1280px) 1200px, 100vw"
                alt={item.title}
                className="w-full h-full object-cover"
              />
            </div>
          )}

          <article className={MEASURE}>
            <div className={`${EYEBROW} ${T.eyebrow} mb-3`}>
              {formatNewsDate(item.publishDate)}
            </div>

            <h1 className={`font-display ${T.heading} text-[32px] md:text-[52px] leading-[1.02] tracking-[-0.03em] text-balance wrap-break-word hyphens-auto`}>
              {item.title}
            </h1>

            {item.bodyHtml && (
              <div
                // Long-form copy carries more weight than a section's muted text (t.body at 65%),
                // which is sized for a paragraph or two rather than a whole article.
                className={`${RICHTEXT} text-mf-black/80 mt-7 md:mt-9 pt-7 md:pt-9 border-t ${T.line}`}
                style={{ textWrap: 'pretty' }}
                dangerouslySetInnerHTML={{ __html: item.bodyHtml }}
              />
            )}

            <div className={`mt-10 md:mt-12 pt-7 md:pt-9 border-t ${T.line}`}>
              <BackLink />
            </div>
          </article>
        </div>
      </section>
      </main>

      <CmsNewsletterSection {...NEWSLETTER} />
      <AppBand showGuitar={false} heading="Hent vores Midtfyns Festival App!" subtitle={null} />

      <Footer />
    </>
  );
}

/** Navigation back to the overview — an outline pill, not the coral CTA: leaving the article is
 *  not the thing the page is asking the reader to do. */
function BackLink() {
  return (
    <Link
      to="/nyheder"
      className={`inline-flex items-center gap-2 rounded-full px-5 md:px-6 py-2.5 md:py-3 font-medium text-sm md:text-base transition-colors ${OUTLINE_CONTROL[T.outline]}`}
    >
      ← Tilbage
    </Link>
  );
}
